// src/app/services/notification.service.ts
import { Injectable, inject, PLATFORM_ID, NgZone } from '@angular/core'; // Importar NgZone
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import { serverTimestamp } from 'firebase/firestore';
import { FirestoreService } from './firestore.service';
import { AuthService } from './auth.service';
// Asegúrate que el nombre del servicio de Toast sea correcto
import { ToastServiceService } from './toast-service.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private messaging?: Messaging;
  private currentChatId: string | null = null; // ID del chat actualmente abierto por el usuario
  private currentToken?: string; // Token FCM actual del dispositivo/navegador

  // Inyectar dependencias necesarias
  private firebaseApp = inject(FirebaseApp);
  private firestoreService = inject(FirestoreService);
  private authService = inject(AuthService);
  private toastService = inject(ToastServiceService); // Tu servicio de Toast
  private router = inject(Router);
  private ngZone = inject(NgZone); // Inyectar NgZone

  constructor() {
    if (this.isBrowser) {
      try {
        this.messaging = getMessaging(this.firebaseApp);
        this.watchRoute();
        console.log('[NotificationService] messaging initialized');
        // No llamar a escucharMensajes aquí, esperar a que el usuario esté autenticado (en initAuthListener)
      } catch (err) {
        console.error("[NotificationService] Error inicializando Firebase Messaging:", err);
      }
    }

    // Suscripción a eventos de navegación para saber en qué chat estamos
    // (Lógica existente está bien)
    this.router.events
      .pipe(filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd)) // Type guard es más limpio
      .subscribe((evt: NavigationEnd) => {
        // Extraer el ID del chat de la URL
        const match = evt.urlAfterRedirects.match(/\/chat\/([^\/\?]+)/);
        const newChatId = match ? match[1] : null;
        if (newChatId !== this.currentChatId) {
          this.currentChatId = newChatId;
          console.log('[NotificationService] currentChatId actualizado a:', this.currentChatId);
        }
      });
  }

  /** Solicita permiso de notificación al usuario y guarda/actualiza el token FCM */
  async solicitarPermiso(): Promise<void> {
    console.log('[NotificationService] solicitarPermiso() called');
    if (!this.isBrowser || !this.messaging) {
      console.log('[NotificationService] Aborting solicitarPermiso: no browser or no messaging');
      return;
    }

    const uid = this.authService.uid; // Obtener UID ANTES de pedir permiso
    if (!uid) {
      console.warn('[NotificationService] Aborting solicitarPermiso: usuario no autenticado.');
      return;
    }

    try {
      const permiso = await Notification.requestPermission();
      console.log('[NotificationService] Notification.permission =', permiso);
      if (permiso !== 'granted') {
        console.warn('[NotificationService] Permiso de notificación no concedido.');
        // Opcional: Podrías querer limpiar un token antiguo si el permiso se revoca
        // await this.signOutCleanup(); // Considera si esto es deseado
        return;
      }

      // Obtener el token FCM
      const token = await getToken(this.messaging, {
        // Asegúrate que esta VAPID key sea la correcta para tu proyecto Firebase
        vapidKey: 'BIPKnViNuHFbh2Z171JnMmvmF2Cygs-BWZeN8UgZqCs4RAb5uXYbCmQke_nb9mw5nyH_J7SGrRMn9psZtIt3ceU',
      });

      if (!token) {
        console.warn('[NotificationService] getToken() no devolvió un token.');
        return;
      }
      console.log('[NotificationService] FCM token obtenido:', token);

      // Si el token es diferente al que teníamos, o no teníamos ninguno, lo guardamos
      if (token !== this.currentToken) {
        this.currentToken = token; // Actualizar el token local
        const tokenData = {
          token: token,
          createdAt: serverTimestamp(), // Fecha de creación/actualización
          lastSeen: serverTimestamp(), // Última vez visto (actualizar al guardar)
          userAgent: navigator.userAgent // Opcional: guardar info del navegador
        };
        // Guardar el token en Firestore asociado al UID del usuario
        await this.firestoreService.setDoc(
          `usuarios/${uid}/tokenNotifications`, // Colección de tokens por usuario
          token, // Usar el propio token como ID del documento para evitar duplicados
          tokenData
        );
        console.log('[NotificationService] Token guardado/actualizado en Firestore para UID:', uid);
      } else {
        console.log('[NotificationService] El token FCM no ha cambiado.');
        // Opcional: Aunque el token no cambie, podrías actualizar 'lastSeen'
        await this.firestoreService.updateDoc(
          `usuarios/${uid}/tokenNotifications`, token, { lastSeen: serverTimestamp() },
        );
      }

    } catch (err) {
      console.error('[NotificationService] Error en solicitarPermiso:', err);
      // Considerar limpiar el token local si falla la obtención o guardado
      // this.currentToken = undefined;
    }
  }

 

  escucharMensajes(): void {
    if (!this.isBrowser || !this.messaging) return;

    onMessage(this.messaging, payload => {
      const data = payload.data as Record<string, string> || {};
      this.ngZone.run(() => this.dispatchNotification(data));
    });
  }

  private dispatchNotification(data: Record<string, string>) {
    const type = data['type'];
    switch (type) {
      case 'message':
        this.processMessage(data);
        break;
      case 'gift':
        this.processGift(data);
        break;
      default:
        console.warn('[NotificationService] Tipo de notificación desconocido:', type);
    }
  }

  private processMessage(data: Record<string, string>) {
    const fromId = data['fromId']!;
    const body = data['body']!;
    const fromName = data['fromName']!;
    const fotoUrl = data['fotoUrlFrom']!;
    const clickUrl = `/chat/${fromId}`;

    // Si ya estoy en ese chat, no muestro toast
    if (fromId === this.currentChatId) return;

    this.toastService.showMessageNotification(
      fromName,
      fotoUrl,
      body,
      clickUrl,
      3000
    );
  }

  private processGift(data: Record<string, string>) {
    const fromUserName = data['fromUserName']!;
    const nombreRegalo = data['nombreRegalo']!;
    const precio = data['precio']!;
    const fotoRegaloUrl = data['fotoRegaloUrl']!;
    const clickUrl = `/app/regalos-recibidos`;

    this.toastService.showGiftNotification(
      fromUserName,
      fotoRegaloUrl,
      `${nombreRegalo} (💰${precio})`,
      clickUrl,
      5000
    );
  }

  private watchRoute() {
    this.router.events.subscribe(evt => {
      if (evt instanceof NavigationEnd) {
        const match = evt.urlAfterRedirects.match(/\/chat\/([^\/\?]+)/);
        this.currentChatId = match ? match[1] : null;
      }
    });
  }

  /** Inicia el listener de estado de autenticación */
  initAuthListener(): () => void {
    console.log('[NotificationService] initAuthListener() llamado.');
    // Suscribirse a cambios en el estado de autenticación
    const unsubscribe = this.authService.onAuthStateChanged(user => {
      this.ngZone.run(() => { // Ejecutar dentro de la zona por si acaso afecta UI indirectamente
        console.log('[NotificationService] Auth state changed. User:', user ? user.uid : 'null');
        if (user) {
          // Usuario autenticado: solicitar permiso y empezar a escuchar mensajes
          this.solicitarPermiso(); // Intentará obtener/actualizar token y guardarlo
          this.escucharMensajes(); // Adjuntar listener onMessage
        } else {
          // Usuario no autenticado (logout): limpiar token
          console.log('[NotificationService] Usuario deslogueado, ejecutando limpieza.');
          this.signOutCleanup(); // Eliminar token de FCM y Firestore
        }
      });
    });

    // Devolver la función para desuscribirse del listener de auth
    return unsubscribe;
  }

  /** Limpia el token FCM al cerrar sesión */
  async signOutCleanup(): Promise<void> {
    console.log('[NotificationService] signOutCleanup() llamado.');
    // Guardar referencia al token actual antes de limpiarlo
    const tokenToClean = this.currentToken;
    const uid = this.authService.uid; // El UID debería ser null aquí, pero lo obtenemos por si acaso

    // Limpiar el token local inmediatamente
    this.currentToken = undefined;

    // Intentar eliminar el token de FCM
    if (this.isBrowser && this.messaging) {
      try {
        await deleteToken(this.messaging);
        console.log('[NotificationService] Token FCM eliminado exitosamente (deleteToken).');
      } catch (error) {
        console.warn('[NotificationService] Error al eliminar el token FCM (deleteToken):', error);
        // Continuar para intentar eliminar de Firestore de todas formas
      }
    }

    // Si teníamos un UID y un token asociados, intentar eliminarlo de Firestore
    if (uid && tokenToClean) {
      try {
        await this.firestoreService.deleteDoc(
          `usuarios/${uid}/tokenNotifications`, // Path a la colección de tokens
          tokenToClean // ID del documento (el propio token)
        );
        console.log('[NotificationService] Token eliminado de Firestore para UID:', uid);
      } catch (error) {
        console.warn(`[NotificationService] Error al eliminar token ${tokenToClean} de Firestore para UID ${uid}:`, error);
      }
    } else {
      if (!tokenToClean) console.log('[NotificationService] No había token local para limpiar de Firestore.');
      if (!uid) console.log('[NotificationService] No había UID para limpiar token de Firestore (esto es normal en logout).');
    }
  }
}